import { useMutation, UseMutationOptions, useQueryClient } from '@tanstack/react-query';
import { CancelGenerationResultDto } from '@tinct/core-api-models';
import { ApiError, tinctApi } from '../../api';

export interface CancelGenerationParams {
  id: string;
}

export const cancelGeneration = async (params: CancelGenerationParams): Promise<CancelGenerationResultDto> => {
  return await tinctApi.campaigns.cancelGeneration(params.id);
};

/**
 * Cancels the campaign's queued generation jobs. Cancelling spends no credits,
 * so no organization/quota invalidation; the cancelled pages go back to
 * `waiting`, which both the campaign projections and the pages list show.
 */
export const useCancelGeneration = (
  options?: Omit<
    UseMutationOptions<CancelGenerationResultDto, ApiError, CancelGenerationParams>,
    'mutationFn'
  >,
) => {
  const { onSuccess, ...restOptions } = options || {};
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: cancelGeneration,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      queryClient.invalidateQueries({ queryKey: ['campaign', args[1].id] });
      queryClient.invalidateQueries({ queryKey: ['pages'] });
      onSuccess?.(...args);
    },
    ...restOptions,
  });
};
